import usePersistFn from '@/hooks/usePersistFn';
import { useAppearanceConfigModelStore } from '@/models/appearance-config/appearanceConfig';
import { useDocsDataModelStore } from '@/models/docs-data/docsData';
import { useEnvConfigModelStore } from '@/models/env-config/envConfig';
import type { AppTheme } from '@/shared/constant/AppTheme';
import { DocsSiteVisitType } from '@/shared/constant/DocsIdTypeData';
import { OnlineType } from '@/shared/constant/OnlineType';
import { TypeInRoute } from '@/shared/constant/TypeInRoute';
import { buildResourcePath } from '@/shared/constant/resource-path/BuildResourcePathByConfig';
import { customDomainRequestPath } from '@/shared/request/customDomainRequestPath';
import { DataSchemaContext as MarkdownSchemaContext } from '@/ui-kit/components/MarkdownSafeMdxViewer/Components/DataSchema/DataSchemaContext';
import InnerAsyncMarkdownViewer from '@/ui-kit/components/MarkdownSafeMdxViewer/viewer';
import type { IDataSchemaDefinitions } from '@tarslib/types/lib/dataSchema';
import clsx from 'clsx';
import trim from 'lodash-es/trim';
import React from 'react';
import styles from './MarkdownViewer.module.css';
import useAppApiServerHostnames from './hooks/useAppApiServerHostname';

export type MarkdownViewerProps = {
  value: string;
  enableHeaderPlugin: boolean;
  inDocView: boolean;
  className?: string;
  theme?: AppTheme;
};

export function MarkdownViewer(props: MarkdownViewerProps) {
  const { value, enableHeaderPlugin, inDocView, className, theme: themeProp } = props;

  const locale = useDocsDataModelStore((x) => x.docsBaseConfig.language);
  const onlineType = useDocsDataModelStore((x) => x.docsIdTypeData.onlineType);
  const appLowerName = useEnvConfigModelStore((x) => x.APP_NAME_LOWER);
  const appApiServerHostnames = useAppApiServerHostnames();
  const dataSchemaDefinitionMap = useDocsDataModelStore(
    (x) => x.resourceData.extraData.dataSchemaDefinitions,
  );
  const docsIdTypeData = useDocsDataModelStore((x) => x.docsIdTypeData);
  const appTheme = useAppearanceConfigModelStore((x) => x.appTheme);
  const theme = useDocsDataModelStore((x) => x.docsBaseConfig.theme);
  const itemPathPrefix = useDocsDataModelStore((x) => x.itemPathPrefix);
  const seoInfos = useDocsDataModelStore((x) => x.seoInfos);
  const projectSetting = useDocsDataModelStore((x) => x.projectSetting);

  const imageReplaceHostOptions = React.useMemo(() => {
    return {
      searchHostname: appApiServerHostnames,
      replaceHostname: '',
      // 支持自定义域名图片替换
      customDomainRequestPath: (() => {
        if (
          docsIdTypeData.onlineType === OnlineType.DocsSite &&
          docsIdTypeData.visitType === DocsSiteVisitType.CustomDomain
        ) {
          const urlPrefix = (docsIdTypeData.subdirectory + customDomainRequestPath).trim();
          if (urlPrefix) {
            return '/' + trim(urlPrefix, '/');
          }
          return urlPrefix;
        }
        return '';
      })(),
      isEnterpriseEdition: IS_PDV,
    };
  }, [appApiServerHostnames, docsIdTypeData]);

  const imageSrcAddParams = React.useMemo(() => {
    if (IS_PDV) {
      return undefined;
    }

    return {
      searchHostnames: appApiServerHostnames,
      onlineType,
      locale: locale,
    };
  }, [appApiServerHostnames, locale, onlineType]);

  const urlToCurrentDocUrl = React.useCallback(
    (url: string) => {
      const matchResult = url.match(/^(doc|endpoint|api|websocket|folder|socket-io)-(\d+)$/);
      if (matchResult) {
        const [, typeString = '', idString = ''] = matchResult;
        const id = Number(idString);
        const map: Record<string, TypeInRoute | undefined> = {
          doc: TypeInRoute.Doc,
          endpoint: TypeInRoute.Api,
          api: TypeInRoute.Api,
          websocket: TypeInRoute.WebSocketApi,
          folder: TypeInRoute.ApiFolder,
          'socket-io': TypeInRoute.SocketIOApi,
        };
        const type = map[typeString];
        if (typeString && idString && Number.isInteger(id) && type) {
          const newUrl = buildResourcePath(
            {
              docsIdTypeData,
              itemPathPrefix: itemPathPrefix ?? {
                baseUrl: '',
                versionSlug: '',
                subdirectory: '',
                isSharedDocIndependentDomain: false,
              },
              seoInfos: seoInfos ?? new Map(),
              docsSiteSummary: {
                summary: {
                  projectSetting,
                },
              },
            },
            {
              id,
              type,
              name: '',
            },
            undefined,
          );
          return newUrl;
        }
      }

      return url;
    },
    [docsIdTypeData, itemPathPrefix, projectSetting, seoInfos],
  );

  const markdownSchemaContextValue = React.useMemo(() => {
    return (dataSchemaDefinitionMap || {}) as IDataSchemaDefinitions;
  }, [dataSchemaDefinitionMap]);

  if (!value) {
    return null;
  }
  return (
    <div className={clsx(styles['markdown-container'], className)}>
      <MarkdownSchemaContext.Provider value={markdownSchemaContextValue}>
        <InnerAsyncMarkdownViewer
          value={value}
          renderExternalLink={urlToCurrentDocUrl}
          openExternalLink={undefined}
          imageReplaceHostOptions={imageReplaceHostOptions}
          imageSrcAddParams={imageSrcAddParams}
          showErrorMsg={false}
          locale={locale}
          enableDataSchema
          enableHeadingAutoLink={enableHeaderPlugin}
          enableHeadingSlugId={enableHeaderPlugin}
          inDocView={inDocView}
          slugIdPrefix=""
          appLowerName={appLowerName}
          theme={themeProp ?? appTheme ?? theme}
        />
      </MarkdownSchemaContext.Provider>
    </div>
  );
}
